import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/ProductCard";
import { Stars } from "@/components/Stars";
import { swatchStyle } from "@/constants/colors";
import { tierRows } from "@/lib/pricing";
import { ShoppingBag, Minus, Plus, ArrowLeft, Check, Star } from "@phosphor-icons/react";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1572726729207-a78d6feb18d7?q=80&w=800";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [reviewsData, setReviewsData] = useState({ reviews: [], average: 0, count: 0 });
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reviewMsg, setReviewMsg] = useState("");

  const loadReviews = useCallback(() => {
    api.get(`/products/${id}/reviews`).then((r) => setReviewsData(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setQty(1); setMyRating(0); setMyComment(""); setReviewMsg("");
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      api.get("/products", { params: { department: r.data.department } })
        .then((rr) => setRelated(rr.data.filter((p) => p.id !== id).slice(0, 3)));
    }).catch(() => navigate("/shop"));
    loadReviews();
  }, [id, navigate, loadReviews]);

  if (!product) return <div className="min-h-[60vh] flex items-center justify-center text-stone-400">Cargando...</div>;

  const onSale = product.sale_price && product.sale_price > 0 && product.sale_price < product.price;
  const discount = onSale ? Math.round((1 - product.sale_price / product.price) * 100) : 0;
  const priceRows = tierRows(product);
  const activeRow = priceRows.reduce((acc, r, i) => (qty >= r.min ? i : acc), 0);

  const handleAdd = () => {
    addItem(product, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { navigate(`/login?redirect=/product/${id}`); return; }
    if (myRating < 1) { setReviewMsg("Por favor elija de 1 a 5 estrellas."); return; }
    setSubmitting(true); setReviewMsg("");
    try {
      await api.post(`/products/${id}/reviews`, { rating: myRating, comment: myComment.trim() });
      setMyRating(0); setMyComment("");
      setReviewMsg("¡Gracias por su reseña!");
      loadReviews();
      api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => {});
    } catch (err) {
      setReviewMsg("No se pudo guardar la reseña. Intente de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10" data-testid="product-detail-page">
      <Link to="/shop" className="inline-flex items-center gap-2 label-caps text-stone-500 hover:text-stone-900 mb-8"><ArrowLeft size={14} /> Volver</Link>
      <div className="grid md:grid-cols-2 gap-12 lg:gap-20">
        <div className="relative overflow-hidden bg-[#F0EFEB] aspect-[4/5]">
          {onSale && <span className="absolute top-4 left-4 z-10 bg-[#991B1B] text-white text-xs tracking-wider px-3 py-1.5 uppercase" data-testid="detail-sale-badge">Venta -{discount}%</span>}
          <img src={product.images?.[0] || FALLBACK_IMG} alt={product.name} className="w-full h-full object-cover" data-testid="product-image" />
        </div>
        <div className="flex flex-col justify-center max-w-md">
          <div className="label-caps text-[#C0A062] mb-3">{product.department}{product.category ? ` · ${product.category}` : ""}</div>
          <h1 className="font-serif text-4xl lg:text-5xl text-stone-900 tracking-tight leading-tight" data-testid="product-name">{product.name}</h1>
          {reviewsData.count > 0 && (
            <div className="flex items-center gap-2 mt-4" data-testid="detail-rating">
              <Stars value={reviewsData.average} size={18} />
              <span className="text-sm text-stone-500">{reviewsData.average} · {reviewsData.count} reseña{reviewsData.count !== 1 ? "s" : ""}</span>
            </div>
          )}
          <div className="mt-6" data-testid="product-price">
            {onSale ? (
              <div className="flex items-baseline gap-3 flex-wrap">
                <span className="text-3xl text-[#991B1B] font-medium">${product.sale_price.toFixed(2)}</span>
                <span className="text-xl text-stone-400 line-through">${product.price.toFixed(2)}</span>
                <span className="text-xs bg-[#991B1B] text-white px-2 py-0.5 uppercase tracking-wide">-{discount}%</span>
              </div>
            ) : (
              <span className="text-3xl text-stone-900 font-medium">${product.price?.toFixed(2)}</span>
            )}
          </div>
          {priceRows.length > 0 && (
            <div className="mt-5 border border-stone-200 rounded-lg overflow-hidden" data-testid="tier-price-table">
              <div className="bg-[#3F4E4F] text-white px-4 py-2 text-xs tracking-wider uppercase">Precios por cantidad · ¡Compre más y ahorre!</div>
              {priceRows.map((r, i) => (
                <div key={i} className={`flex justify-between px-4 py-2.5 text-sm ${i > 0 ? "border-t border-stone-100" : ""} ${i === activeRow ? "bg-[#C0A062]/15 font-medium text-stone-900" : "text-stone-600"}`} data-testid={`tier-table-row-${i}`}>
                  <span>{r.label}</span>
                  <span>${r.price.toFixed(2)} c/u</span>
                </div>
              ))}
            </div>
          )}
          <p className="text-stone-600 leading-relaxed mt-6" data-testid="product-description">{product.description}</p>
          <div className="text-sm text-stone-500 mt-4">
            {product.stock > 0 ? <span className="text-[#166534]">● En stock ({product.stock} disponibles)</span> : <span className="text-[#991B1B]">Agotado</span>}
          </div>

          {product.colors?.length > 0 && (
            <div className="mt-6" data-testid="detail-colors">
              <div className="label-caps text-stone-500 mb-2">Colores disponibles</div>
              <div className="flex flex-wrap gap-3">
                {product.colors.map((c) => (
                  <div key={c} className="flex items-center gap-2 text-sm text-stone-600" data-testid={`detail-color-${c}`}>
                    <span className="inline-block w-5 h-5 rounded-full border border-stone-300" style={swatchStyle(c)} /> {c}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-4 mt-8">
            <div className="flex items-center border border-stone-300">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3 hover:bg-stone-100" data-testid="qty-decrease"><Minus size={16} /></button>
              <span className="px-5 text-stone-900" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-4 py-3 hover:bg-stone-100" data-testid="qty-increase"><Plus size={16} /></button>
            </div>
            <button onClick={handleAdd} disabled={product.stock <= 0} className="flex-1 flex items-center justify-center gap-2 bg-[#3F4E4F] text-white px-6 py-4 hover:bg-[#2C3637] transition-colors disabled:opacity-40" data-testid="add-to-bag-button">
              {added ? <><Check size={18} /> Agregado</> : <><ShoppingBag size={18} /> Agregar al carrito</>}
            </button>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section className="mt-24 max-w-3xl" data-testid="reviews-section">
        <h2 className="font-serif text-3xl text-stone-900 mb-2">Reseñas de clientes</h2>
        {reviewsData.count > 0 ? (
          <div className="flex items-center gap-3 mb-8">
            <Stars value={reviewsData.average} size={20} />
            <span className="text-stone-600">{reviewsData.average} de 5 · {reviewsData.count} reseña{reviewsData.count !== 1 ? "s" : ""}</span>
          </div>
        ) : (
          <p className="text-stone-500 mb-8">Aún no hay reseñas. ¡Sé el primero en opinar!</p>
        )}

        <form onSubmit={submitReview} className="bg-white border border-stone-200 rounded-lg p-6 mb-10" data-testid="review-form">
          <div className="label-caps text-stone-500 mb-3">Deja tu reseña</div>
          <div className="flex items-center gap-1 mb-4">
            {[1, 2, 3, 4, 5].map((n) => (
              <button type="button" key={n} onClick={() => setMyRating(n)} className="text-[#C0A062] hover:scale-110 transition-transform" data-testid={`rating-star-${n}`} aria-label={`${n} estrellas`}>
                <Star size={28} weight={n <= myRating ? "fill" : "regular"} />
              </button>
            ))}
          </div>
          <textarea value={myComment} onChange={(e) => setMyComment(e.target.value)} rows={3} placeholder="Cuéntanos qué te pareció el producto (opcional)" className="w-full border border-stone-300 px-4 py-3 outline-none focus:border-[#3F4E4F]" data-testid="review-comment" />
          {reviewMsg && <div className="text-sm mt-2 text-[#3F4E4F]" data-testid="review-message">{reviewMsg}</div>}
          <button type="submit" disabled={submitting} className="mt-4 bg-[#3F4E4F] text-white px-6 py-3 hover:bg-[#2C3637] disabled:opacity-50" data-testid="submit-review-button">
            {submitting ? "Enviando..." : user ? "Publicar reseña" : "Inicia sesión para opinar"}
          </button>
        </form>

        <div className="space-y-6" data-testid="reviews-list">
          {reviewsData.reviews.map((rv) => (
            <div key={rv.id} className="border-b border-stone-100 pb-6" data-testid={`review-${rv.id}`}>
              <div className="flex items-center gap-3 mb-1">
                <span className="font-medium text-stone-900">{rv.user_name}</span>
                <Stars value={rv.rating} size={14} />
              </div>
              {rv.comment && <p className="text-stone-600 leading-relaxed">{rv.comment}</p>}
            </div>
          ))}
        </div>
      </section>

      {related.length > 0 && (
        <section className="mt-24">
          <h2 className="font-serif text-3xl text-stone-900 mb-10">También te puede gustar</h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
